import React from 'react';

/**
 * FilterAwarenessBar — Shared sticky awareness banner for all CRM list pages.
 *
 * Displays a contextual banner that clearly communicates when filtered data is
 * being shown. Extracted from the Analysis page pattern and made reusable.
 *
 * Usage:
 *   <FilterAwarenessBar
 *     title="Leads is showing a filtered view"
 *     summary="Filtered by 3 criteria"
 *     detail="Period: This Month · Stage: Prospect · Branch: Jakarta"
 *     loading={isLoading}
 *     activeCount={3}
 *     filterTags={[
 *       { key: 'period', label: 'This Month' },
 *       { key: 'stage', label: 'Stage: Prospect' },
 *     ]}
 *     onRemoveFilter={(key) => handleRemove(key)}
 *     onResetFilters={handleReset}
 *   />
 *
 * Props:
 *   title       {string}   — Bold headline (e.g. "Leads is showing a filtered view")
 *   summary     {string}   — One-liner summary (e.g. "Filtered by 3 criteria")
 *   detail      {string}   — Optional comma-separated detail (e.g. "Period: This Month · Stage: Prospect")
 *   loading     {boolean}  — Shows "Refreshing…" pulse state
 *   activeCount {number}   — Number of active filters (0 hides the bar)
 *   filterTags  {Array}    — [{ key, label }] chips to render
 *   onRemoveFilter {fn}    — Called with (key) when × is clicked
 *   onResetFilters {fn}    — Called when "Reset all" is clicked
 */
const FilterAwarenessBar = ({
  title,
  summary,
  detail,
  loading = false,
  activeCount = 0,
  filterTags = [],
  onRemoveFilter,
  onResetFilters,
}) => {
  if (activeCount === 0 && filterTags.length === 0) return null;

  return (
    <div className="crm-filter-sticky-stack">
      {/* ── Awareness banner ── */}
      <div className="crm-awareness-bar">
        <div className="crm-awareness-copy">
          <div className="crm-awareness-kicker">
            <span className={`crm-awareness-dot${loading ? ' is-loading' : ''}`} />
            <span>{loading ? 'Refreshing Data' : 'Data Filter'}</span>
          </div>
          {title  && <div className="crm-awareness-title">{title}</div>}
          {summary && <div className="crm-awareness-summary">{summary}</div>}
          {detail  && <div className="crm-awareness-detail">{detail}</div>}
        </div>
      </div>

      {/* ── Dismissible filter chips ── */}
      {filterTags.length > 0 && (
        <div className="crm-filter-chips">
          {filterTags.map((tag) => (
            <span key={tag.key} className="crm-filter-chip">
              {tag.label}
              {onRemoveFilter && (
                <button
                  type="button"
                  className="crm-filter-chip-remove"
                  aria-label={`Remove ${tag.label} filter`}
                  onClick={() => onRemoveFilter(tag.key)}
                >
                  ×
                </button>
              )}
            </span>
          ))}
          {onResetFilters && filterTags.length > 1 && (
            <button
              type="button"
              className="crm-filter-chip-reset"
              onClick={onResetFilters}
            >
              Reset all
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default FilterAwarenessBar;

/* ─────────────────────────────────────────────────────────────────────────────
 * Shared CSS — inject once via FilterAwarenessBar.css or paste into global CSS.
 * Below are the styles as a JS template string for easy copy-paste if needed.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * .crm-filter-sticky-stack {
 *   position: sticky;
 *   top: 0;
 *   z-index: 1025;
 *   margin-bottom: 18px;
 *   padding-top: 6px;
 *   background: linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(255,255,255,0.92) 82%, rgba(255,255,255,0) 100%);
 *   backdrop-filter: blur(10px);
 * }
 * .crm-awareness-bar {
 *   display: flex;
 *   gap: 16px;
 *   margin-bottom: 10px;
 *   padding: 14px 18px;
 *   border-radius: 20px;
 *   border: 1px solid rgba(148, 163, 184, 0.2);
 *   background: linear-gradient(135deg, rgba(248,250,252,0.98), rgba(239,246,255,0.96));
 *   box-shadow: 0 14px 34px rgba(15, 23, 42, 0.08);
 * }
 * .crm-awareness-kicker {
 *   display: inline-flex;
 *   align-items: center;
 *   gap: 8px;
 *   font-size: 11px;
 *   font-weight: 800;
 *   letter-spacing: 0.08em;
 *   text-transform: uppercase;
 *   color: #0369a1;
 *   margin-bottom: 4px;
 * }
 * .crm-awareness-dot {
 *   width: 8px;
 *   height: 8px;
 *   border-radius: 999px;
 *   background: #14b8a6;
 *   box-shadow: 0 0 0 6px rgba(20,184,166,0.12);
 *   flex-shrink: 0;
 * }
 * .crm-awareness-dot.is-loading {
 *   background: #f97316;
 *   box-shadow: 0 0 0 6px rgba(249,115,22,0.14);
 *   animation: crmPulse 1.2s ease-in-out infinite;
 * }
 * @keyframes crmPulse {
 *   0%, 100% { opacity: 1; transform: scale(1); }
 *   50% { opacity: 0.6; transform: scale(1.3); }
 * }
 * .crm-awareness-title {
 *   font-size: 1rem;
 *   font-weight: 700;
 *   color: #0f172a;
 *   margin-bottom: 4px;
 * }
 * .crm-awareness-summary {
 *   font-size: 13px;
 *   color: #334155;
 *   line-height: 1.6;
 * }
 * .crm-awareness-detail {
 *   margin-top: 6px;
 *   font-size: 12px;
 *   color: #475569;
 *   line-height: 1.5;
 * }
 * .crm-filter-chips {
 *   display: flex;
 *   flex-wrap: wrap;
 *   gap: 6px;
 *   padding: 0 2px 10px;
 * }
 * .crm-filter-chip {
 *   display: inline-flex;
 *   align-items: center;
 *   gap: 6px;
 *   padding: 4px 10px;
 *   border-radius: 999px;
 *   border: 1px solid #cbd5e1;
 *   background: #f8fafc;
 *   font-size: 12px;
 *   font-weight: 500;
 *   color: #334155;
 * }
 * .crm-filter-chip-remove {
 *   border: none;
 *   background: none;
 *   padding: 0;
 *   line-height: 1;
 *   font-size: 14px;
 *   color: #94a3b8;
 *   cursor: pointer;
 * }
 * .crm-filter-chip-remove:hover { color: #ef4444; }
 * .crm-filter-chip-reset {
 *   border: none;
 *   background: none;
 *   padding: 4px 10px;
 *   font-size: 12px;
 *   font-weight: 600;
 *   color: #0369a1;
 *   cursor: pointer;
 *   text-decoration: underline;
 * }
 */
